
import Vue from "vue";
import AppAssessmentListingFilterHeader from "@/components/recruiter/app_assessment/AppAssessmentListingFilterHeader.vue";
import GoBackHeader from "@/components/shared/GoBackHeader.vue";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { AppAssessmentNames } from "@/store/modules/common/interfaces";
import { mapActions, mapGetters, mapMutations } from "vuex";
import DataNotFound from "@/components/shared/DataNotFound.vue";
import {
  APP_ASSESSMENTS,
  SITE_DIRECTION
} from "@/store/modules/common/constants";
import { AssessmentListingHeaderOptions } from "@/interfaces/responses/assessments/assessments";
import {
  ACTIVE_ASSESSMENT,
  APP_ENG_ASSESSMENT_LISTING,
  RESET_ASSESSMENT,
  APPROVE_ASSESSMENT_RESUME,
  REJECT_ASSESSMENT_RESUME
} from "@/store/modules/recruiter/constants";
import { SiteDirections } from "@/store/modules/common/interfaces";
import {
  format_date,
  get_app_assessment_status,
  get_app_assessment_status_color,
  get_color_for_percentage,
  get_pass_or_fail_class,
  assessment_pass_fail_result
} from "@/utils/global";
import { get_color_by_name } from "@/utils/colors";
import {
  calculateScoreForFilter,
  calculateEnglishAssessmentScore
} from "@/utils/assessment.utils";
import { SearchUserObject } from "@/interfaces/shared/auto_complete_user_search";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import DeleteItem from "@/components/shared/DeleteItem.vue";
import { AppEngAssessmentListing } from "@/store/modules/recruiter/interfaces";
import { User } from "@/interfaces/data_objects/user";
import OnBoardingInvitationCard from "@/components/recruiter/OnBoardingInvitationCard.vue";
import { USER_DETAILS_BY_ID } from "@/store/modules/auth/constants";
import RefreshButton from "@/components/shared/RefreshButton.vue";
import { ENG_ASSESSMENT_SORT_CONFIG } from "@/utils/assessment_sort_config";

export default Vue.extend({
  name: "AppEngAssessmentReport",
  components: {
    AppAssessmentListingFilterHeader,
    GoBackHeader,
    DataNotFound,
    DeleteItem,
    OnBoardingInvitationCard,
    RefreshButton
  },
  data() {
    return {
      loading: false, // Loading state of the page
      data_error: false, // Error state of the page
      assessment: null as AppAssessments.Assessment | null, // Assessment object
      table_headers: [
        {
          title: this.$t("assessments.listing.col-1").toString(),
          cols: 1,
          sortable: false,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-4").toString(),
          cols: 2,
          sortable: true,
          direction: "desc"
        },
        { title: this.$t("assessments.listing.col-2").toString(), cols: 2 },
        {
          title: this.$t("assessments.listing.col-score").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-3").toString(),
          cols: 3,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-5").toString(),
          cols: 2,
          is_btn: true
        }
      ] as AssessmentListingHeaderOptions[],
      sort_field_config: ENG_ASSESSMENT_SORT_CONFIG,
      delete_icon: require("@/assets/icons/bold/reset.svg"),
      redo_icon: require("@/assets/icons/bold/reset.svg"),
      is_delete: false,
      selected_user: {
        id: 0,
        last_name: "",
        first_name: "",
        avatar_uri: ""
      } as AppAssessments.AssessmentUserObject,
      delete_btn_loading: false,
      version: "v1" as string,
      on_boarding_invitation: false,
      user_object: {} as User,
      status: ""
    };
  },
  beforeRouteEnter(to, from, next) {
    const version = to.params.version;
    if (
      version === "v1" ||
      version === "v2" ||
      version === "v3" ||
      version === "v4" ||
      version === "v5" ||
      version === "genx_easy_v1" ||
      version === "genx_medium_v1" ||
      version === "technician_v1" ||
      version === "lead_technician_v1" ||
      version === "assistant_engineer_v1"
    ) {
      next();
    } else {
      next({ name: "eng-assessment-not-found" });
    }
  },
  async beforeRouteUpdate(to, from, next) {
    const version = to.params.version;
    if (
      version === "v1" ||
      version === "v2" ||
      version === "v3" ||
      version === "v4" ||
      version === "v5" ||
      version === "genx_easy_v1" ||
      version === "genx_medium_v1" ||
      version === "technician_v1" ||
      version === "lead_technician_v1" ||
      version === "assistant_engineer_v1"
    ) {
      this.version = version;
      await this.init_process();
      next();
    } else {
      next({ name: "eng-assessment-not-found" });
    }
  },
  created() {
    this.version = this.$route.params.version || "v1";
  },
  computed: {
    SiteDirections() {
      return SiteDirections;
    },
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS,
      get_site_direction: SITE_DIRECTION
    }),
    ...mapGetters("recruiter", {
      app_eng_assessment_listing: APP_ENG_ASSESSMENT_LISTING
    }),
    not_found_error() {
      return this.$t("errors.english-assessments-not-found").toString();
    },
    AppAssessmentsStatus() {
      return AppAssessments.AppAssessmentStatus;
    },
    CurrentAssessment(): AppEngAssessmentListing {
      return this.app_eng_assessment_listing(this.version);
    }
  },
  watch: {
    "CurrentAssessment.selected_dates.date_filter": function (
      new_value: string[],
      old_value: string[]
    ) {
      if (
        (new_value.length === 2 || new_value.length === 0) &&
        new_value.length !== old_value.length
      )
        this.process_assessment_listing(1);
    },
    "CurrentAssessment.selected_candidates": function (
      new_value: SearchUserObject[],
      old_value: SearchUserObject[]
    ) {
      if (new_value.length !== old_value.length)
        this.process_assessment_listing(1);
    },
    get_site_direction() {
      this.table_headers = [
        {
          title: this.$t("assessments.listing.col-1").toString(),
          cols: 1,
          sortable: false,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-4").toString(),

          cols: 2,
          sortable: true,
          direction: "asc"
        },
        { title: this.$t("assessments.listing.col-2").toString(), cols: 2 },
        {
          title: this.$t("assessments.listing.col-score").toString(),
          cols: 2,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-3").toString(),
          cols: 3,
          sortable: true,
          direction: "asc"
        },
        {
          title: this.$t("assessments.listing.col-5").toString(),
          cols: 2,
          is_btn: true
        }
      ];
    },
    app_assessments: {
      handler(newVal) {
        console.log(
          "EngReport: app_assessments watcher triggered, length:",
          newVal ? newVal.length : 0
        );
        this.init_process();
      },
      immediate: true
    },
    version: {
      handler(newVal) {
        console.log("EngReport: version watcher triggered:", newVal);
        this.init_process();
      }
    }
  },
  async mounted() {
    console.log("EngReport: mounted, version:", this.version);
    this.init_process();
  },
  methods: {
    format_date,
    get_app_assessment_status,
    get_app_assessment_status_color,
    get_color_for_percentage,
    get_pass_or_fail_class,
    assessment_pass_fail_result,
    ...mapActions("recruiter", {
      fetch_eng_assessment_listing: APP_ENG_ASSESSMENT_LISTING,
      delete_user_english_assessment: RESET_ASSESSMENT
    }),
    ...mapMutations("recruiter", {
      set_active_assessment: ACTIVE_ASSESSMENT
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION,
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("auth", {
      fetch_user_by_id: USER_DETAILS_BY_ID
    }),
    ...mapActions("common", {
      fetch_app_assessments: APP_ASSESSMENTS
    }),
    async init_process() {
      console.log("EngReport: init_process started, version:", this.version);
      this.loading = true;
      this.data_error = false;

      // If assessments are not loaded yet, fetch them
      if (!this.app_assessments || this.app_assessments.length === 0) {
        console.log("EngReport: app_assessments empty, fetching...");
        await this.fetch_app_assessments();
      }

      console.log("EngReport: app_assessments in store:", this.app_assessments);
      let assessment_to_find = "";
      // this.version === "v1"
      //   ? AppAssessmentNames.english_v1
      //   : AppAssessmentNames.english_v2;
      switch (this.version) {
        case "v1":
          assessment_to_find = AppAssessmentNames.english_v1;
          break;
        case "v2":
          assessment_to_find = AppAssessmentNames.english_v2;
          break;
        case "v3":
          assessment_to_find = AppAssessmentNames.english_v3;
          break;
        case "v4":
          assessment_to_find = AppAssessmentNames.english_v4;
          break;
        case "v5":
          assessment_to_find = AppAssessmentNames.english_v5;
          break;
        case "genx_easy_v1":
          assessment_to_find = AppAssessmentNames.english_genx_easy_v1;
          break;
        case "genx_medium_v1":
          assessment_to_find = AppAssessmentNames.english_genx_medium_v1;
          break;
        case "technician_v1":
          assessment_to_find = AppAssessmentNames.english_technician_v1;
          break;
        case "lead_technician_v1":
          assessment_to_find = AppAssessmentNames.english_lead_technician_v1;
          break;
        case "assistant_engineer_v1":
          assessment_to_find = AppAssessmentNames.english_assistant_engineer_v1;
          break;
        default:
          throw new Error("Unsupported version");
      }
      console.log(
        "EngReport: searching for assessment matching:",
        assessment_to_find
      );
      this.assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) =>
          val.assessment_type === assessment_to_find
      );

      // Fallback: search by name if type doesn't match
      if (!this.assessment) {
        console.log(
          "EngReport: Assessment type not found, trying fallback search by name..."
        );
        this.assessment = this.app_assessments.find(
          (val: AppAssessments.Assessment) =>
            val.assessment_name.toLowerCase().includes(this.version) &&
            val.assessment_name.toLowerCase().includes("english")
        );
      }

      console.log("EngReport: final found assessment object:", this.assessment);

      // If assessment is still not found
      if (!this.assessment) {
        console.warn(
          "EngReport: assessment not found in store for version:",
          this.version
        );
        this.data_error = true;
        this.loading = false;
        return;
      }
      console.log(
        "EngReport: setting active assessment and fetching candidates..."
      );
      this.set_active_assessment({
        assessment_type: this.assessment.assessment_type,
        assessment_id: this.assessment.id
      });
      // Fetch assessment listing
      await this.fetch_assessment_by_pagination(
        this.CurrentAssessment.page,
        this.CurrentAssessment.limit,
        this.assessment.id
      );
    },
    async process_assessment_listing(page: number) {
      if (!this.assessment) return;
      await this.fetch_assessment_by_pagination(
        page,
        this.CurrentAssessment.limit,
        this.assessment.id
      );
    },
    /**
     * Fetch assessment listing
     * @param page Page number
     * @param limit Limit of the page
     * @param assessment_id Assessment id
     */
    async fetch_assessment_by_pagination(
      page = 1,
      limit: number,
      assessment_id: number
    ) {
      this.loading = true;
      const res = await this.fetch_eng_assessment_listing({
        page,
        limit,
        assessment_id,
        selected_dates: this.CurrentAssessment.selected_dates,
        selected_candidates: this.CurrentAssessment.selected_candidates,
        version: this.version,
        score_filter: this.CurrentAssessment.score_filter,
        status_filter: this.CurrentAssessment.status_filter,
        sort_by: this.CurrentAssessment.sort_by,
        sort_order: this.CurrentAssessment.sort_order
      });
      if (!res) {
        console.error(
          "EngReport: fetch_eng_assessment_listing returned false (error)"
        );
        this.data_error = true;
        this.loading = false;
        return;
      }
      this.loading = false;
    },
    transform_view_icon(icon: HTMLElement): HTMLElement {
      const color = get_color_by_name("success");
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    view_assessment_result(assessment_result_id: number, user_id: number) {
      this.$router.push(
        `/recruiter/assessments/english/${this.version}/${assessment_result_id}/view/${user_id}`
      );
    },
    transform_trash_icon(icon: HTMLElement): HTMLElement {
      const color = "#FF4066";
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    transform_redo_icon(icon: HTMLElement): HTMLElement {
      const color = "#FF4066";
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    redo_assessment(user: AppAssessments.AssessmentUserObject) {
      this.selected_user = user;
      this.delete_btn_loading = false;
      this.is_delete = true;
    },
    async dialog_action(value: boolean) {
      if (value) {
        this.delete_btn_loading = true;
        const response = await this.delete_user_english_assessment({
          assessment_id: this.assessment?.id,
          user_id: this.selected_user.id
        });
        if (response) {
          this.set_root_notification(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset")}`
          );
          this.process_assessment_listing(1);
        } else {
          this.set_root_error(
            `${this.selected_user.first_name} ${
              this.selected_user.last_name
            } ${this.$t("assessments.assessment-reset-failed")}`
          );
        }
        this.is_delete = false;
      }
    },
    update_filter(filter: string) {
      this.CurrentAssessment.score_filter = filter;
      this.process_assessment_listing(1);
    },
    get_assessment(assessment: AppAssessments.AssessmentResults) {
      return (
        assessment.status !== AppAssessments.AppAssessmentStatus.IN_PROGRESS &&
        assessment.status !==
          AppAssessments.AppAssessmentStatus.RESULT_PROCESSING
      );
    },
    async sort_assessment(ind: number) {
      // Toggle direction
      this.table_headers[ind].direction =
        this.table_headers[ind].direction === "asc" ? "desc" : "asc";

      // Map column index to backend sort field using configuration
      const sortField = this.sort_field_config[ind];

      if (sortField && this.assessment?.id) {
        // Update sort state
        this.CurrentAssessment.sort_by = sortField;
        this.CurrentAssessment.sort_order = this.table_headers[ind].direction;

        // Fetch data with new sort parameters
        await this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit,
          this.assessment.id
        );
      }
    },
    async open_user_details(
      user: AppAssessments.AssessmentUserObject,
      status: AppAssessments.AppAssessmentStatus | null
    ) {
      const response = await this.fetch_user_by_id(user.id);
      if (response) {
        this.user_object = response;
        this.on_boarding_invitation = true;
        this.status = status || "";
      }
    },
    update_status_filter(filter: string) {
      this.CurrentAssessment.status_filter = filter;
      this.process_assessment_listing(1);
    },
    check_status(assessment: AppAssessments.AssessmentResults) {
      if (assessment.status)
        return [
          AppAssessments.AppAssessmentStatus.IN_PROGRESS,
          AppAssessments.AppAssessmentStatus.CANCELLED,
          AppAssessments.AppAssessmentStatus.FAILED,
          AppAssessments.AppAssessmentStatus.REDO
        ].includes(assessment.status);
      return false;
    },
    refresh_listing() {
      if (this.assessment?.id)
        this.fetch_assessment_by_pagination(
          this.CurrentAssessment.page,
          this.CurrentAssessment.limit,
          this.assessment?.id
        );
    },
    /**
     * Calculate overall score for an assessment
     * Uses centralized helper to ensure consistency across all pages
     * @param assessment - The assessment result object
     */
    calculate_assessment_score(
      assessment: AppAssessments.AssessmentResults
    ): number {
      if (!assessment || !assessment.result) return 0;
      return calculateEnglishAssessmentScore(assessment.result);
    },
    get_status_label(assessment: AppAssessments.AssessmentResults) {
      // "Left Incomplete" only when candidate left without submitting (IN_PROGRESS + PENDING approval).
      // RESULT_PROCESSING = candidate submitted; show "Result Processing" even before score is computed.
      if (
        assessment.status === AppAssessments.AppAssessmentStatus.IN_PROGRESS &&
        assessment.resume_admin_status === "PENDING"
      ) {
        return this.$t("assessments.left-incomplete").toString();
      }
      return assessment.status
        ? this.get_app_assessment_status(assessment.status)
        : "";
    },
    can_approve_resume(assessment: AppAssessments.AssessmentResults) {
      // Show Approve Resume only for "Left Incomplete" (candidate left without submitting, PENDING approval).
      // Do not show for "Result Processing" (candidate has submitted).
      return (
        assessment.status === AppAssessments.AppAssessmentStatus.IN_PROGRESS &&
        assessment.resume_admin_status === "PENDING"
      );
    },
    async approve_resume(assessment: AppAssessments.AssessmentResults) {
      const ok = (await this.$store.dispatch(
        "recruiter/" + APPROVE_ASSESSMENT_RESUME,
        assessment.id
      )) as boolean;
      if (ok) {
        this.set_root_notification(
          this.$t("assessments.resume-approved").toString()
        );
        this.refresh_listing();
      } else {
        this.set_root_error(
          this.$t("assessments.resume-approve-failed").toString()
        );
      }
    },
    async reject_resume(assessment: AppAssessments.AssessmentResults) {
      const ok = (await this.$store.dispatch(
        "recruiter/" + REJECT_ASSESSMENT_RESUME,
        assessment.id
      )) as boolean;
      if (ok) {
        this.set_root_notification(
          this.$t("assessments.resume-rejected").toString()
        );
        this.refresh_listing();
      } else {
        this.set_root_error(
          this.$t("assessments.resume-reject-failed").toString()
        );
      }
    },
    /**
     * Calculate score based on coherence values for a specific question filter type
     * @param details - Assessment result details
     * @param filter - The question filter type
     */
    /**
     * Calculate score for a filter - uses centralized helper
     */
    calculate_coherence_score_for_filter(
      details: AppAssessments.AssessmentResultDetails[],
      filter: AppAssessments.QuestionFilter
    ): number | null {
      return calculateScoreForFilter(details, filter);
    }
  }
});
